import { MenuItem } from '@shopify/hydrogen/storefront-api-types'
import { createContext, Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react'
import { EnhancedMenuItem } from '../../lib/menus'
import { MotionValue, useMotionValue } from 'framer-motion'
import { useCart } from '@shopify/hydrogen'
import { useCookie } from '../../lib/hooks/cookie.client'

interface HeaderContextProps {
  activeAdditionalMenuItem?: MenuItem
  activeChildMenuItem?: EnhancedMenuItem
  activeDesktopParentMenuIndex?: number
  activeMobileParentMenuIndex?: number
  burgerIconOpacity: MotionValue<number>
  hoveredParentMenuItem?: EnhancedMenuItem
  isCartDrawerOpen: boolean
  isFilterDrawerOpen: boolean
  isMainNavigationOpen: boolean
  notificationBarBoundingClientRect?: DOMRect
  setActiveAdditionalMenuItem: Dispatch<SetStateAction<MenuItem | undefined>>
  setActiveChildMenuItem: Dispatch<SetStateAction<EnhancedMenuItem | undefined>>
  setActiveDesktopParentMenuIndex: Dispatch<SetStateAction<number | undefined>>
  setActiveMobileParentMenuIndex: Dispatch<SetStateAction<number | undefined>>
  setHoveredParentMenuItem: Dispatch<SetStateAction<EnhancedMenuItem | undefined>>
  setIsCartDrawerOpen: Dispatch<SetStateAction<boolean>>
  setIsFilterDrawerOpen: Dispatch<SetStateAction<boolean>>
  setIsMainNavigationOpen: Dispatch<SetStateAction<boolean>>
  setNotificationBarBoundingClientRect: Dispatch<SetStateAction<DOMRect | undefined>>
}

export const HeaderContext = createContext({} as HeaderContextProps)

export const HeaderProvider: React.FC<{
  children: ReactNode
}> = ({ children }) => {
  const { setSessionCookie } = useCookie()
  const { id } = useCart()
  const [activeAdditionalMenuItem, setActiveAdditionalMenuItem] = useState<MenuItem>()
  const [activeChildMenuItem, setActiveChildMenuItem] = useState<EnhancedMenuItem>()
  const [activeDesktopParentMenuIndex, setActiveDesktopParentMenuIndex] = useState<number | undefined>(0)
  const [activeMobileParentMenuIndex, setActiveMobileParentMenuIndex] = useState<number>()
  const burgerIconOpacity = useMotionValue(1)
  const [hoveredParentMenuItem, setHoveredParentMenuItem] = useState<EnhancedMenuItem>()
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const [isMainNavigationOpen, setIsMainNavigationOpen] = useState(false)
  const [notificationBarBoundingClientRect, setNotificationBarBoundingClientRect] = useState<DOMRect>()

  useEffect(() => {
    const lock = isCartDrawerOpen || isFilterDrawerOpen || isMainNavigationOpen
    document.body.style.overflow = lock ? 'hidden' : ''
    
    return () => {
      document.body.style.overflow = ''
    }
  }, [isCartDrawerOpen, isFilterDrawerOpen, isMainNavigationOpen])  

  useEffect(() => {
    if (id) {
      setSessionCookie('cartId', id)
    }
  }, [id])

  return (
    <HeaderContext.Provider
      value={{
        activeAdditionalMenuItem,
        activeChildMenuItem,
        activeDesktopParentMenuIndex,
        activeMobileParentMenuIndex,
        burgerIconOpacity,
        hoveredParentMenuItem,
        isCartDrawerOpen,
        isFilterDrawerOpen,
        isMainNavigationOpen,
        notificationBarBoundingClientRect,
        setActiveAdditionalMenuItem,
        setActiveChildMenuItem,
        setActiveDesktopParentMenuIndex,
        setActiveMobileParentMenuIndex,
        setHoveredParentMenuItem,
        setIsCartDrawerOpen,
        setIsFilterDrawerOpen,
        setIsMainNavigationOpen,
        setNotificationBarBoundingClientRect,
      }}>
      {children}
    </HeaderContext.Provider>
  )
}
